/**
 * The Sources panel (UC-A2), from `design/new design /legacy/bank_ai_assistant_chat`.
 *
 * ## Two things at two breakpoints, from one element
 *
 * The design's own source: `fixed inset-y-0 right-0 translate-x-full … lg:relative
 * lg:translate-x-0 lg:w-[380px]`. Below `lg` it is a slide-over drawer with a scrim; at `lg` and
 * above it is the third column, always present. One element rather than two, so the expanded
 * source and its scroll position survive a window resize — an agent who rotates a tablet mid-read
 * should not lose their place.
 *
 * ## Why a citation is a card and not a list row
 *
 * The employee is about to repeat something to a customer. What decides whether they can is the
 * *passage*, the *version* and the *effective date* — so the expanded card shows the quoted text
 * with its version line above it, and the collapsed one shows enough to choose between two
 * documents. A row of titles would make them open each in turn to find out.
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { StatusPill, cx, type Citation } from "@nab/shared";

export interface SourcePanelProps {
  citations: Citation[];
  /** The expanded one, or null. Set by clicking a `[n]` marker in the answer. */
  selectedIndex: number | null;
  onSelect: (index: number | null) => void;
  /** Below `lg` the panel is a drawer, and this is whether it is open. */
  open: boolean;
  onClose: () => void;
}

export function SourcePanel({
  citations,
  selectedIndex,
  onSelect,
  open,
  onClose,
}: SourcePanelProps): JSX.Element | null {
  const { t } = useTranslation("app");
  const { t: common } = useTranslation();

  // Escape closes the drawer. Only while it is open, and only below `lg` in effect — at desktop
  // width the panel is a column and closing it is the header's toggle, not a reflex.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (citations.length === 0) return null;

  return (
    <>
      {/* The scrim. `lg:hidden` because above that the panel is a column and there is nothing to
          dim. Rendered always so the fade has something to animate from. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className={cx(
          "fixed inset-0 z-[100] bg-surfaceInverse/50 backdrop-blur-sm transition-opacity lg:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      <aside
        aria-label={common("citations.heading")}
        className={cx(
          "fixed inset-y-0 end-0 z-[101] flex h-full w-[85vw] max-w-[400px] flex-col border-s border-borderSubtle bg-surfaceSunken shadow-2xl transition-transform duration-300",
          // The column at `lg`: in flow, no shadow, and never translated away.
          "lg:relative lg:z-20 lg:w-[380px] lg:translate-x-0 lg:shadow-none",
          open ? "translate-x-0" : "translate-x-full rtl:-translate-x-full",
        )}
      >
        <header className="flex h-[64px] shrink-0 items-center justify-between border-b border-borderStrong bg-surfaceRaised px-4 lg:bg-surfaceSunken lg:px-6">
          <div className="flex items-center gap-2">
            <h2 className="text-body-md font-bold tracking-tight text-primary">
              {common("citations.heading")}
            </h2>
            <span className="rounded-full border border-primaryBorder bg-primarySubtle px-2 py-1 text-label-sm font-bold text-primary">
              {citations.length}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            title={common("actions.close")}
            aria-label={common("actions.close")}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-borderStrong bg-surfaceRaised text-textMuted shadow-sm transition-colors hover:text-primary"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 lg:p-6">
          {citations.map((citation) => {
            const expanded = citation.index === selectedIndex;
            return (
              <article
                key={`${citation.index}-${citation.chunk_id ?? ""}`}
                className={cx(
                  "overflow-hidden bg-surfaceRaised shadow-sm transition-all",
                  expanded
                    ? "rounded-[20px] border border-borderSubtle"
                    : "cursor-pointer rounded-2xl border border-surfaceSunken hover:border-borderStrong",
                )}
              >
                <button
                  type="button"
                  onClick={() => onSelect(expanded ? null : citation.index)}
                  aria-expanded={expanded}
                  className={cx(
                    "flex w-full items-start justify-between gap-3 p-3 text-start",
                    expanded && "border-b border-surfaceMuted",
                  )}
                >
                  <span className="flex items-center gap-3">
                    <span
                      className={cx(
                        "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-label-sm font-bold",
                        // The cited-and-open one is the accent; the rest are quiet. Colour is not
                        // the only signal — the number is inside it either way.
                        expanded
                          ? "bg-accent text-onPrimary"
                          : "bg-surfaceSunken text-textMuted",
                      )}
                    >
                      {citation.index}
                    </span>
                    <span className="min-w-0">
                      <span className="line-clamp-1 block text-body-sm font-bold text-textPrimary">
                        <bdi>{citation.document_title}</bdi>
                      </span>
                      {citation.section ? (
                        <span className="mt-1 block text-label-sm font-medium text-textMuted">
                          <bdi>{citation.section}</bdi>
                        </span>
                      ) : null}
                    </span>
                  </span>
                  <span aria-hidden="true" className="text-textMuted">
                    {expanded ? "▴" : "▾"}
                  </span>
                </button>

                {expanded ? (
                  <div className="p-3">
                    <div className="mb-2 flex items-center justify-between text-label-sm text-textMuted">
                      {/* Version and effective date carry the weight here: UC-A2 is about an
                          employee quoting a superseded tariff, and this line is what stops it. */}
                      <span className="font-semibold" dir="ltr">
                        {[
                          citation.version
                            ? common("citations.version", { version: citation.version })
                            : "",
                          citation.effective_date
                            ? common("citations.effectiveFrom", {
                                date: citation.effective_date,
                              })
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" • ")}
                      </span>
                      {citation.page ? (
                        <span className="font-semibold">
                          {t("sources.page", { page: citation.page })}
                        </span>
                      ) : null}
                    </div>
                    {citation.passage ? (
                      <blockquote className="rounded-xl border border-borderStrong bg-surface p-4 text-body-sm leading-relaxed text-textPrimary">
                        <bdi>{citation.passage}</bdi>
                      </blockquote>
                    ) : null}
                    {citation.has_previous_version ? (
                      <div className="mt-2">
                        <StatusPill tone="warning">
                          {common("citations.previousVersionExists")}
                        </StatusPill>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </aside>
    </>
  );
}
